// @givmo/board — "Record the result on the roster": an election's result reaches the roster by the
// officer's own act (CBG-YR3-ELECT, frame t5; the backend's contract `board-terms.md` §7).
//
// Plan §3.6: Board never makes a governance decision, so a carried motion or an executed consent
// changes nothing on the roster by itself, and Board never reads its words for names. The officer
// opens this from the record, CHOOSES the people it elected, and confirms each one: a director's term
// (start, end, class, the counting tick) or an officer's title and its dates, each prefilled from the
// board's own recorded rules with the source said (`record-result.ts`). One save records them all,
// each linked to the record, with the seats' history; the motion, the consent and the minutes don't
// change. The backend refuses only what the record can't elect or the roster can't take (a motion not
// recorded as carried, a consent not executed, someone who isn't a director or no longer serves) and
// never for content — a term past the board's limit is saved as entered (§7.2). Its sentence shows as
// served. Every word was approved in copy round CBG-YR3-COPY1 (Jonathan, 2026-10-14 21:07 PDT).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type Query } from '@tanstack/react-query';
import { isGivmoError, type OrgBoardMemberOut } from '@givmo/api-client';
import {
  Button,
  Checkbox,
  Dialog,
  ErrorMessage,
  FOCUS_RING,
  Menu,
  Select,
  TextField,
  toast,
} from '@givmo/design-system';
import { Plus, X } from 'lucide-react';
import { useBoardOperations } from '../../board-runtime';
import { displayDate } from '../../lib/display';
import { extractParameters } from '../parameters/parameters-decode';
import { useOfficerTitleCatalog } from '../roster/officer-facts-fields';
import { officerRolesL2Words as l2 } from '../roster/officer-roles-l2-words';
import { isDirector } from '../roster/officer-roles';
import { SeatFormDialog } from '../roster/seat-form-dialog';
import { isCommitteeOnlySeat } from '../roster/roster-decode';
import { activeClasses, extractDirectorClasses, termRule } from '../roster/terms-decode';
import { termsWords as tw } from '../roster/terms-words';
import {
  directorDraft,
  officerDraft,
  officerWithFrom,
  officerWithTo,
  resultBody,
  resultProblem,
  withEnd,
  withStart,
  type DirectorDraft,
  type OfficerDraft,
  type ResultRules,
  type ResultSource,
} from './record-result';

const QUIET = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';
const GROUP = 'font-sans text-s12 font-w600 tracking-givmo text-text-secondary';
const NOBODY = '';

/** Whether a cached query reads the roster or one seat: the generated list and detail reads (either
 *  door's), and the member page's own key. Every one of them shows what this act changes. */
export function readsTheRoster(query: Pick<Query, 'queryKey'>): boolean {
  const head = query.queryKey[0];
  if (head === 'board-member') return true;
  if (typeof head !== 'object' || head === null) return false;
  const id = (head as { _id?: unknown })._id;
  return typeof id === 'string' && /(ListBoardMembers|GetBoardMember)$/.test(id);
}

/** The record the result comes from, as the dialog shows it above the people. */
export interface ResultRecord {
  source: ResultSource;
  /** The motion's words verbatim, or the consent's title. */
  words: string;
  /** "Carried · Mar 11, 2027" / "Executed · Mar 11, 2027". */
  standing: string;
  /** The day the prefills start from: the meeting's, or the consent's execution day. */
  day: string;
}

/** A chosen person's name, with a small × that takes them off the list (a wrong pick), kept to the
 *  name's own line height so the rows keep the frame's rhythm. */
function RowHeader({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <div className="flex items-center justify-between gap-8">
      <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">{name}</span>
      <button
        type="button"
        aria-label={tw.removeRow(name)}
        onClick={onRemove}
        className={`-my-2 rounded-sm p-2 text-text-tertiary transition-colors hover:text-text-strong ${FOCUS_RING}`}
      >
        <X aria-hidden className="size-14" />
      </button>
    </div>
  );
}

/** The people offered: those the agenda item Board suggested named, in its order, then the rest of
 *  the roster in its own (the derivation's §3.2). */
export function namedFirst(
  seats: readonly OrgBoardMemberOut[],
  named: readonly string[],
): { named: OrgBoardMemberOut[]; rest: OrgBoardMemberOut[] } {
  const place = new Map(named.map((id, index) => [id, index]));
  return {
    named: seats
      .filter((seat) => place.has(seat.id))
      .sort((left, right) => (place.get(left.id) ?? 0) - (place.get(right.id) ?? 0)),
    rest: seats.filter((seat) => !place.has(seat.id)),
  };
}

/** "+ Add a director" / "+ Add an officer": a link that opens the people to choose from — the
 *  suggestion's first, under its own label — and, for directors, "Add someone new" (frame t5). */
function AddPersonMenu({
  label,
  people,
  onPick,
  onNew,
  testId,
}: {
  label: string;
  people: { named: OrgBoardMemberOut[]; rest: OrgBoardMemberOut[] };
  onPick: (seat: OrgBoardMemberOut) => void;
  onNew?: () => void;
  testId: string;
}) {
  const item = (seat: OrgBoardMemberOut) => (
    <Menu.Item saas key={seat.id} onClick={() => onPick(seat)} testId={`${testId}-${seat.id}`}>
      {seat.display_name}
    </Menu.Item>
  );
  const anyone = people.named.length + people.rest.length > 0;
  return (
    <Menu.Root>
      <Menu.Trigger
        render={
          <Button
            appearance="saas"
            tone="link"
            size="sm"
            className="self-start"
            leadingIcon={<Plus aria-hidden className="size-14" />}
            testId={testId}
          />
        }
      >
        {label}
      </Menu.Trigger>
      <Menu.Content
        saas
        align="start"
        className="max-h-[320px] min-w-[240px] overflow-y-auto"
        testId={`${testId}-menu`}
      >
        {people.named.length > 0 ? (
          <Menu.Group>
            <Menu.GroupLabel saas>{tw.fromTheTerms}</Menu.GroupLabel>
            {people.named.map(item)}
          </Menu.Group>
        ) : null}
        {people.named.length > 0 && people.rest.length > 0 ? (
          <Menu.Separator className="my-4 bg-border-subtle" />
        ) : null}
        {people.rest.map(item)}
        {onNew !== undefined ? (
          <>
            {anyone ? <Menu.Separator className="my-4 bg-border-subtle" /> : null}
            <Menu.Item saas onClick={onNew} testId={`${testId}-new`}>
              {tw.addSomeoneNew}
            </Menu.Item>
          </>
        ) : null}
      </Menu.Content>
    </Menu.Root>
  );
}

export function RecordResultDialog({
  orgRef,
  record,
  roster,
  named = { directors: [], officers: [] },
  onClose,
  onRecorded,
}: {
  orgRef: string;
  record: ResultRecord;
  roster: readonly OrgBoardMemberOut[];
  /** The seats the suggestion's agenda item named, offered first. */
  named?: { directors: readonly string[]; officers: readonly string[] };
  onClose: () => void;
  onRecorded: () => void;
}) {
  const operations = useBoardOperations();
  const queryClient = useQueryClient();
  const parametersQuery = useQuery({
    ...operations.officerListBoardGovernanceParametersOptions({ path: { org_ref: orgRef } }),
    select: extractParameters,
  });
  const classesQuery = useQuery({
    ...operations.officerListBoardDirectorClassesOptions({ path: { org_ref: orgRef } }),
    select: extractDirectorClasses,
  });
  const titles = useOfficerTitleCatalog(orgRef);
  const classes = useMemo(() => activeClasses(classesQuery.data ?? []), [classesQuery.data]);
  const rules = useMemo<ResultRules>(() => {
    const parameters = parametersQuery.data ?? [];
    const length = termRule(parameters, 'director_term_length');
    const limit = termRule(parameters, 'director_term_limit');
    const officer = termRule(parameters, 'officer_term_length');
    return {
      lengthYears: length?.value ?? null,
      lengthCitation: length?.citation ?? null,
      limit: limit?.value ?? null,
      limitCitation: limit?.citation ?? null,
      officerYears: officer?.value ?? null,
      officerCitation: officer?.citation ?? null,
    };
  }, [parametersQuery.data]);

  const [directors, setDirectors] = useState<DirectorDraft[]>([]);
  const [officers, setOfficers] = useState<OfficerDraft[]>([]);
  const [addingSomeone, setAddingSomeone] = useState(false);
  const [newSeats, setNewSeats] = useState<OrgBoardMemberOut[]>([]);
  const [error, setError] = useState<string | null>(null);

  // CBG-YR6-OUTSIDE (edge 12): an election puts people on the board, so a seat for someone who
  // isn't on it is never offered (it holds no title and is never a director).
  const serving = useMemo(
    () =>
      [...roster, ...newSeats].filter(
        (seat) => seat.status === 'active' && !isCommitteeOnlySeat(seat),
      ),
    [roster, newSeats],
  );
  const directorChoices = namedFirst(
    serving.filter((seat) => isDirector(seat) && !directors.some((row) => row.seatId === seat.id)),
    named.directors,
  );
  const officerChoices = namedFirst(serving, named.officers);

  const addDirector = (seat: OrgBoardMemberOut) =>
    setDirectors((rows) => [...rows, directorDraft(seat, record.day, rules, classes)]);
  const addOfficer = (seat: OrgBoardMemberOut) =>
    setOfficers((rows) => [...rows, officerDraft(seat, record.day, rules)]);
  const updateDirector = (index: number, next: DirectorDraft) =>
    setDirectors((rows) => rows.map((row, at) => (at === index ? next : row)));
  const updateOfficer = (index: number, next: OfficerDraft) =>
    setOfficers((rows) => rows.map((row, at) => (at === index ? next : row)));

  const save = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardRosterResult({
        path: { org_ref: orgRef },
        body: resultBody(record.source, directors, officers, titles.catalog),
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success(tw.resultRecorded);
      void queryClient.invalidateQueries({ predicate: readsTheRoster });
      onRecorded();
    },
    onError: (caught) => setError(isGivmoError(caught) ? caught.message : tw.resultFailed),
  });

  const problem = resultProblem(directors, officers);
  const nothingYet = directors.length === 0 && officers.length === 0;

  return (
    <>
      <Dialog.Root open onOpenChange={(next) => !next && !save.isPending && onClose()}>
        <Dialog.Content
          aria-label={tw.recordResult}
          width={560}
          className="flex max-h-[86vh] flex-col gap-16 overflow-y-auto"
          testId="record-result-dialog"
        >
          <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
            {tw.recordResult}
          </Dialog.Title>

          {/* The record as recorded: its words verbatim and its standing. A leading rule marks it as
              the record this act hangs from. */}
          <div className="flex flex-col gap-4 border-l-2 border-border-strong pl-12">
            <p className="whitespace-pre-wrap font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {record.words}
            </p>
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {record.standing}
            </span>
          </div>

          <div className="flex flex-col gap-4" data-testid="record-result-directors">
            <span className={GROUP}>{tw.directorsElected}</span>
            {directors.length > 0 ? (
              <ul className="flex flex-col">
                {directors.map((row, index) => (
                  <li
                    key={row.seatId}
                    className="flex flex-col gap-10 border-t border-border-subtle py-12 first:border-t-0 first:pt-4"
                    data-testid={`record-result-director-${row.seatId}`}
                  >
                    <RowHeader
                      name={row.name}
                      onRemove={() => setDirectors((rows) => rows.filter((_, at) => at !== index))}
                    />
                    <div
                      className={`grid gap-10 max-sm:grid-cols-2 ${classes.length > 0 ? 'grid-cols-[1fr_1fr_0.8fr]' : 'grid-cols-2'}`}
                    >
                      <TextField
                        label={tw.start}
                        type="date"
                        value={row.startsOn}
                        onChange={(event) =>
                          updateDirector(index, withStart(row, event.target.value, rules))
                        }
                        testId={`record-result-start-${row.seatId}`}
                      />
                      <TextField
                        label={tw.end}
                        type="date"
                        value={row.endsOn}
                        onChange={(event) =>
                          updateDirector(index, withEnd(row, event.target.value))
                        }
                        testId={`record-result-end-${row.seatId}`}
                      />
                      {classes.length > 0 ? (
                        <Select
                          label={tw.classLabel}
                          appearance="saas"
                          value={row.classId ?? 'none'}
                          onValueChange={(next) =>
                            updateDirector(index, {
                              ...row,
                              classId: next === null || next === 'none' ? null : next,
                            })
                          }
                          options={[
                            { value: 'none', label: tw.noClass },
                            ...classes.map((option) => ({ value: option.id, label: option.label })),
                          ]}
                          testId={`record-result-class-${row.seatId}`}
                        />
                      ) : null}
                    </div>
                    {row.endFromLength &&
                    rules.lengthYears !== null &&
                    rules.lengthCitation !== null ? (
                      <span
                        className={`-mt-4 ${QUIET}`}
                        data-testid={`record-result-end-source-${row.seatId}`}
                      >
                        {tw.endFromLength(rules.lengthYears, rules.lengthCitation)}
                      </span>
                    ) : null}
                    {rules.limit !== null && rules.limitCitation !== null ? (
                      <div className="flex flex-col gap-4">
                        <label className="inline-flex cursor-pointer items-center gap-10 font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
                          <Checkbox
                            appearance="saas"
                            checked={row.counts}
                            onCheckedChange={(next) =>
                              updateDirector(index, { ...row, counts: next === true })
                            }
                            testId={`record-result-counts-${row.seatId}`}
                          />
                          {tw.countsTowardLimit}
                        </label>
                        {row.counts ? (
                          <span
                            className={`pl-[30px] ${QUIET}`}
                            data-testid={`record-result-counted-${row.seatId}`}
                          >
                            {tw.withThisOne(
                              row.countedBefore + 1,
                              rules.limit,
                              rules.limitCitation,
                            )}
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            <AddPersonMenu
              label={tw.addDirector}
              people={directorChoices}
              onPick={addDirector}
              onNew={() => setAddingSomeone(true)}
              testId="record-result-add-director"
            />
          </div>

          <div className="flex flex-col gap-4" data-testid="record-result-officers">
            <span className={GROUP}>{tw.officersElected}</span>
            {officers.length > 0 ? (
              <ul className="flex flex-col">
                {officers.map((row, index) => (
                  <li
                    key={`${row.seatId}:${index}`}
                    className="flex flex-col gap-10 border-t border-border-subtle py-12 first:border-t-0 first:pt-4"
                    data-testid={`record-result-officer-${row.seatId}`}
                  >
                    <RowHeader
                      name={row.name}
                      onRemove={() => setOfficers((rows) => rows.filter((_, at) => at !== index))}
                    />
                    <div className="grid grid-cols-[1fr_1fr_1fr] gap-10 max-sm:grid-cols-2">
                      <Select
                        label={tw.title}
                        appearance="saas"
                        placeholder={tw.title}
                        value={row.titleKey ?? NOBODY}
                        onValueChange={(next) =>
                          updateOfficer(index, { ...row, titleKey: next ?? null })
                        }
                        options={titles.catalog.map((title) => ({
                          value: title.key,
                          label: title.label,
                        }))}
                        testId={`record-result-title-${row.seatId}`}
                      />
                      <TextField
                        label={l2.heldFrom}
                        type="date"
                        value={row.heldFrom}
                        onChange={(event) =>
                          updateOfficer(index, officerWithFrom(row, event.target.value, rules))
                        }
                        testId={`record-result-held-from-${row.seatId}`}
                      />
                      <TextField
                        label={l2.heldTo}
                        type="date"
                        value={row.heldTo}
                        onChange={(event) =>
                          updateOfficer(index, officerWithTo(row, event.target.value))
                        }
                        testId={`record-result-held-to-${row.seatId}`}
                      />
                    </div>
                    {row.heldToFromLength &&
                    rules.officerYears !== null &&
                    rules.officerCitation !== null ? (
                      <span className={`-mt-4 ${QUIET}`}>
                        {tw.endFromLength(rules.officerYears, rules.officerCitation)}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            <AddPersonMenu
              label={tw.addOfficer}
              people={officerChoices}
              onPick={addOfficer}
              testId="record-result-add-officer"
            />
          </div>

          <p className={QUIET}>
            {'motionId' in record.source ? tw.resultNoteMotion : tw.resultNoteConsent}
          </p>

          {error !== null ? (
            <ErrorMessage testId="record-result-error">{error}</ErrorMessage>
          ) : null}
          {!nothingYet && problem !== null ? (
            <span className={QUIET} data-testid="record-result-problem">
              {problem}
            </span>
          ) : null}

          <div className="flex justify-end gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              disabled={save.isPending}
              onClick={onClose}
              testId="record-result-cancel"
            >
              Cancel
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              loading={save.isPending}
              disabled={save.isPending || problem !== null}
              onClick={() => {
                setError(null);
                save.mutate();
              }}
              testId="record-result-save"
            >
              {tw.recordOnRoster}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      {addingSomeone ? (
        <SeatFormDialog
          orgRef={orgRef}
          mode="create"
          seat={null}
          withoutTerm
          onClose={() => setAddingSomeone(false)}
          onSaved={() => setAddingSomeone(false)}
          onCreated={(seat) => {
            setNewSeats((seats) => [...seats, seat]);
            if (isDirector(seat)) addDirector(seat);
          }}
        />
      ) : null}
    </>
  );
}

/** The standing line of a carried motion: "Carried · {the day it was voted}". */
export function carriedLine(votedAt: string | null | undefined): string {
  return tw.carried(displayDate(votedAt ?? null));
}
